import { useMemo, useState, useCallback } from 'react'
import { Plus } from 'lucide-react'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { CampaignSelector } from '@/components/features/CampaignSelector'
import {
  ListsTable,
  DialogAddList,
  DialogViewLeads,
  DialogViewStats,
  DialogChangeCampaign,
  DialogDeleteList,
} from './ListsTable'
import { useLists } from '@/entities/list'
import { useCampaignSelection } from '@/entities/campaign'
import type { CrmListRow } from '@intone/shared/types'

export function ListsPage() {
  // Campaign state (persisted to session storage)
  const { campaignId: selectedCampaignId, setCampaignId: setSelectedCampaignId, campaigns } = useCampaignSelection()
  const { data: listsData, isLoading, refetch: refetchLists } = useLists()

  // Dialog states
  const [showUploadDialog, setShowUploadDialog] = useState(false)
  const [viewLeadsDialog, setViewLeadsDialog] = useState<CrmListRow | null>(null)
  const [viewStatsDialog, setViewStatsDialog] = useState<CrmListRow | null>(null)
  const [changeCampaignDialog, setChangeCampaignDialog] = useState<CrmListRow | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<CrmListRow | null>(null)

  // Calculate totals based on selected campaign
  const { totalListsCount, totalLeadsCount } = useMemo(() => {
    if (!listsData?.lists) {
      return { totalListsCount: 0, totalLeadsCount: 0 }
    }

    const filtered = selectedCampaignId
      ? listsData.lists.filter((l) => l.campaign_id === selectedCampaignId)
      : listsData.lists

    const totalLeadsCount = filtered.reduce((sum, list) => sum + (list.size || 0), 0)

    return {
      totalListsCount: filtered.length,
      totalLeadsCount,
    }
  }, [listsData?.lists, selectedCampaignId])

  const handleCampaignChange = useCallback((campaignId: number | null) => {
    setSelectedCampaignId(campaignId)
  }, [setSelectedCampaignId])

  return (
    <div className="w-full min-h-screen flex flex-col">
      <div className="p-6 pb-4 flex-shrink-0 border-b">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
          <div>
            <h1 className="text-heading font-bold text-gray-900 mb-1">Lead Manager</h1>
            <p className="text-detail text-gray-600">Create and manage your call lists</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <CampaignSelector
              campaigns={campaigns}
              selectedCampaignId={selectedCampaignId}
              onCampaignChange={handleCampaignChange}
              className="w-full sm:w-80 lg:w-96"
            />
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Summary Cards */}
        <Card className="mb-4">
          <CardContent className="flex flex-col sm:flex-row items-center justify-between gap-4 py-6">
            <div className="flex items-center gap-8">
              <div>
                <div className="text-body font-medium text-gray-500">Total Lists</div>
                <div className="text-heading font-bold">{totalListsCount}</div>
              </div>
              <div>
                <div className="text-body font-medium text-gray-500">Total Leads</div>
                <div className="text-heading font-bold">{totalLeadsCount.toLocaleString()}</div>
              </div>
            </div>
            <Button onClick={() => setShowUploadDialog(true)} className="w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-2" />
              New List
            </Button>
          </CardContent>
        </Card>

        {/* Lists Table */}
        {isLoading ? (
          <div className="flex items-center justify-center h-48 text-gray-500 text-body">
            Loading...
          </div>
        ) : (
          <ListsTable
            lists={listsData?.lists ?? []}
            campaigns={campaigns}
            selectedCampaignId={selectedCampaignId}
            onViewLeads={setViewLeadsDialog}
            onViewStats={setViewStatsDialog}
            onChangeCampaign={setChangeCampaignDialog}
            onDeleteList={setDeleteDialog}
          />
        )}
      </div>

      {/* Dialogs */}
      <DialogAddList
        open={showUploadDialog}
        onOpenChange={(open) => {
          setShowUploadDialog(open)
          if (!open) refetchLists()
        }}
        campaigns={campaigns}
        selectedCampaignId={selectedCampaignId}
      />

      <DialogViewLeads
        open={viewLeadsDialog !== null}
        onOpenChange={(open) => {
          if (!open) {
            setViewLeadsDialog(null)
            refetchLists()
          }
        }}
        list={viewLeadsDialog}
      />

      <DialogViewStats
        open={viewStatsDialog !== null}
        onOpenChange={(open) => !open && setViewStatsDialog(null)}
        list={viewStatsDialog}
      />

      <DialogChangeCampaign
        open={changeCampaignDialog !== null}
        onOpenChange={(open) => {
          if (!open) {
            setChangeCampaignDialog(null)
            refetchLists()
          }
        }}
        list={changeCampaignDialog}
        campaigns={campaigns}
      />

      <DialogDeleteList
        open={deleteDialog !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteDialog(null)
            refetchLists()
          }
        }}
        list={deleteDialog}
      />
    </div>
  )
}